import {
  CircleAlert as ErrorCircle24Regular,
  RefreshCw as ArrowClockwise24Regular,
  RefreshCw as ArrowSync24Regular,
} from "lucide-react";
import { Component, ReactNode, ErrorInfo } from "react";
import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";

import { logger } from "../../services/logger.service";

interface RouteErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  onReset?: () => void;
}

interface RouteErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  isChunkError: boolean;
}

function isChunkLoadError(error: Error | null): boolean {
  if (!error) return false;
  const message = error.message?.toLowerCase() || "";
  const name = error.name?.toLowerCase() || "";
  return (
    message.includes("failed to fetch dynamically imported module") ||
    message.includes("loading chunk") ||
    message.includes("loading css chunk") ||
    message.includes("failed to load module script") ||
    message.includes("preload") ||
    name.includes("chunkloaderror") ||
    (error instanceof TypeError && message.includes("failed to fetch"))
  );
}

class RouteErrorBoundary extends Component<RouteErrorBoundaryProps, RouteErrorBoundaryState> {
  constructor(props: RouteErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): RouteErrorBoundaryState {
    return {
      hasError: true,
      error,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const isChunk = isChunkLoadError(error);
    logger.error("Route error caught by RouteErrorBoundary", error, {
      component: "RouteErrorBoundary",
      componentStack: errorInfo?.componentStack,
      isChunkError: isChunk,
    });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, isChunkError: false });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      const { fallbackTitle = "Something went wrong" } = this.props;
      const { isChunkError } = this.state;

      if (isChunkError) {
        return (
          <div className="flex flex-col justify-center items-center h-full min-h-[300px] w-full gap-4 p-8 text-center bg-surface">
            <ArrowSync24Regular className="w-[48px] h-[48px] text-fg-link" />
            <div className="flex flex-col gap-2">
              <SkyText
                size={500}
                weight="semibold"
                className="text-fg-1"
              >
                New Version Available
              </SkyText>
              <SkyText
                size={300}
                className="text-fg-3 max-w-[400px]"
              >
                A new version of skyAdvisor has been deployed. Please refresh the page to get the
                latest updates.
              </SkyText>
            </div>
            <div className="flex gap-3 mt-2">
              <SkyButton
                appearance="primary"
                icon={ArrowClockwise24Regular}
                onClick={this.handleRefresh}
              >
                Refresh Page
              </SkyButton>
            </div>
          </div>
        );
      }

      return (
        <div className="flex flex-col justify-center items-center h-full min-h-[300px] w-full gap-4 p-8 text-center bg-surface">
          <ErrorCircle24Regular className="text-fg-danger-soft w-[48px] h-[48px]" />
          <div className="flex flex-col gap-2">
            <SkyText
              size={500}
              weight="semibold"
              className="text-fg-1"
            >
              {fallbackTitle}
            </SkyText>
            <SkyText
              size={300}
              className="text-fg-3 max-w-[400px]"
            >
              An error occurred while loading this section. Please try refreshing the page.
            </SkyText>
          </div>
          <div className="flex gap-3 mt-2">
            <SkyButton
              appearance="primary"
              icon={ArrowClockwise24Regular}
              onClick={this.handleRefresh}
            >
              Refresh Page
            </SkyButton>
            <SkyButton
              appearance="secondary"
              icon={ArrowClockwise24Regular}
              onClick={this.handleRetry}
            >
              Try Again
            </SkyButton>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default RouteErrorBoundary;
